import React, { useState, useEffect } from 'react';
import { Users, Shield, UserCog, Search, MoreVertical, Eye, Edit, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabase/client';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'production' | 'field' | 'customer_service' | 'client';
  created_at: string;
  last_sign_in_at?: string;
}

const roleLabels = {
  super_admin: 'Super Admin',
  production: 'Production',
  field: 'Field Person',
  customer_service: 'Customer Service',
  client: 'Client'
};

const roleDescriptions = {
  super_admin: 'Full system access - Can view, edit, and delete everything',
  production: 'Raw materials only - Can view and edit inventory and suppliers',
  field: 'Projects only - Can view and edit projects and clients',
  customer_service: 'Invoices only - Can view and manage invoices and orders',
  client: 'Customer portal - Limited access to view their own data'
};

const roleColors = {
  super_admin: 'bg-purple-500/20 text-purple-500 border-purple-500/30',
  production: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
  field: 'bg-green-500/20 text-green-500 border-green-500/30',
  customer_service: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
  client: 'bg-gray-500/20 text-gray-500 border-gray-500/30'
};

export function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState<User['role']>('client');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Fetch all profiles - RLS policies now fixed
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        // If we can't fetch profiles, show at least the current user
        if (currentUser) {
          setUsers([{
            id: currentUser.id,
            email: currentUser.email,
            name: currentUser.name,
            role: currentUser.role,
            created_at: new Date().toISOString()
          }]);
        }
        return;
      }

      // Map to our user interface
      const mappedUsers: User[] = (profiles || []).map(profile => {
        let role = profile.role as User['role'];
        if (!['super_admin', 'production', 'field', 'customer_service', 'client'].includes(role)) {
          role = 'client';
        }
        return {
          id: profile.id,
          email: profile.email || '',
          name: profile.name || 'Unknown',
          role,
          created_at: profile.created_at
        };
      });

      setUsers(mappedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setIsEditDialogOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;

    try {
      // Call the RPC function to update the role securely
      const { error } = await supabase
        .rpc('update_user_role', {
          user_id_to_update: selectedUser.id,
          new_role: newRole
        });

      if (error) {
        console.error('Error updating profile:', error);
        alert('Failed to update role. Please try again.');
        return;
      }

      // Update local state
      setUsers(users.map(user =>
        user.id === selectedUser.id
          ? { ...user, role: newRole }
          : user
      ));

      setIsEditDialogOpen(false);
      setSelectedUser(null);
      
      alert('Role updated successfully!');
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update role. Please try again.');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      // Delete from auth.users (this should cascade to profiles due to FK constraint)
      const { error } = await supabase.auth.admin.deleteUser(userId);

      if (error) {
        console.error('Error deleting user:', error);
        return;
      }

      setUsers(users.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (currentUser?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don't have permission to access user management.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-enter">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-4xl font-display tracking-tight text-foreground">User Management</h2>
          <p className="text-muted-foreground text-base">Manage user roles and permissions</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="px-3 py-1">
            <Users className="h-3 w-3 mr-1" />
            {users.length} Users
          </Badge>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <div className="grid gap-4">
        {loading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </CardContent>
          </Card>
        ) : filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12 text-muted-foreground">
              No users found
            </CardContent>
          </Card>
        ) : (
          filteredUsers.map((user, index) => (
            <Card
              key={user.id}
              className="glass-card border border-gray-200 light:border-gray-200 hover:shadow-lg transition-all duration-300 animate-enter"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12 border-2 border-white/50 shadow-sm">
                      <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg text-foreground">{user.name}</h3>
                        <Badge className={roleColors[user.role]}>
                          {roleLabels[user.role]}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Created: {new Date(user.created_at).toLocaleDateString()}</span>
                        {user.last_sign_in_at && (
                          <span>Last login: {new Date(user.last_sign_in_at).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" side="bottom" sideOffset={5} className="bg-background border border-border shadow-lg z-[100]">
                        <DropdownMenuItem onClick={() => handleEditUser(user)} className="cursor-pointer">
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Role
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDeleteUser(user.id)}
                          className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="overflow-visible">
          <DialogHeader>
            <DialogTitle>Edit User Role</DialogTitle>
            <DialogDescription>
              Change the role for {selectedUser?.name}. This will update their permissions.
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 border-2 border-white/50 shadow-sm">
                  <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold text-xl">
                    {getInitials(selectedUser.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-lg">{selectedUser.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Current Role</Label>
                <Badge className={roleColors[selectedUser.role]}>
                  {roleLabels[selectedUser.role]}
                </Badge>
                <p className="text-sm text-muted-foreground">
                  {roleDescriptions[selectedUser.role]}
                </p>
              </div>

              <div className="space-y-2">
                <Label>New Role</Label>
                <Select value={newRole} onValueChange={(value: User['role']) => setNewRole(value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent side="bottom" sideOffset={5} collisionPadding={60} className="bg-background border border-border shadow-lg z-[100]">
                    {Object.entries(roleLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value} className="cursor-pointer">
                        <div className="flex items-center gap-2">
                          <span>{label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newRole !== selectedUser.role && (
                  <p className="text-sm text-muted-foreground">
                    {roleDescriptions[newRole]}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateRole}
                  disabled={newRole === selectedUser.role}
                >
                  Update Role
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
